import { Alert, Badge, Button, Checkbox, EmptyState, FieldBlock, Input, LoadingState, useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { OrbindApiError } from '@orbind/api-client'
import { adminSectionToggleCode, moduleToggleCode } from '@orbind/core'
import { FormRow, FormScreen, FormSection } from '../../components/FormScreen.tsx'
import { api } from '../../lib/api.ts'
import { ADMIN_SECTIONS } from '../../mocks/admin.ts'
import { MODULES } from '../../shell/modules.ts'

/**
 * Cadastro de Planos comerciais (SPEC-20260814-1333), no padrao FormScreen
 * (SPEC-20260815-0821).
 *
 * Um plano e um pacote de modulos (toggles ENTITLEMENT). O workspace aponta
 * para um plano na tela de edicao do workspace; aqui se monta a matriz
 * plano x modulos e se acompanha quantos workspaces usam cada pacote.
 */

type PlanList = Awaited<ReturnType<Awaited<ReturnType<(typeof api)['plans']['$get']>>['json']>>
type PlanRow = PlanList['plans'][number]

/**
 * Rotulo amigavel de um toggle ENTITLEMENT: nome do modulo (MODULE_*) ou da
 * tela do admin (ADMIN_*, prefixado para distinguir na matriz do plano).
 * Exportado: os cards de plano do onboarding usam o mesmo rotulo.
 */
export function rotuloDoToggle(code: string): string {
  const module = MODULES.find((m) => moduleToggleCode(m.id) === code)
  if (module) return module.fallbackLabel
  const section = ADMIN_SECTIONS.find((s) => adminSectionToggleCode(s.id) === code)
  if (section) return `Administração · ${section.label}`
  return code
}

type FormPlan = { name: string; description: string; featureToggleIds: readonly string[] }
const FORM_VAZIO: FormPlan = { name: '', description: '', featureToggleIds: [] }

export function PlansSection({ criarSinal = 0 }: { criarSinal?: number }) {
  const toast = useToast()
  const qc = useQueryClient()

  const lista = useQuery({ queryKey: ['plans'], queryFn: async () => (await api.plans.$get()).json() })
  const toggles = useQuery({
    queryKey: ['feature-toggles'],
    queryFn: async () => (await api['feature-toggles'].$get()).json(),
  })

  const [busca, setBusca] = useState('')
  const [editando, setEditando] = useState<{ id: string | null } | null>(null)
  const [form, setForm] = useState<FormPlan>(FORM_VAZIO)
  const [confirmandoExclusao, setConfirmandoExclusao] = useState<string | null>(null)

  useEffect(() => {
    if (criarSinal > 0) {
      setForm(FORM_VAZIO)
      setEditando({ id: null })
    }
  }, [criarSinal])

  const aoFalhar = (erro: unknown) => {
    toast.push({
      tone: 'danger',
      message: erro instanceof Error && erro.message ? erro.message : 'Não foi possível concluir. Tente novamente.',
    })
  }

  /** Plano muda o que os workspaces veem — o bundle tem que refletir. */
  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['plans'] })
    void qc.invalidateQueries({ queryKey: ['feature-toggle-bundle'] })
  }

  const gravarVinculos = async (planId: string, featureToggleIds: readonly string[]) => {
    await api.plans[':id'].toggles.$put({ param: { id: planId }, json: { featureToggleIds: [...featureToggleIds] } })
  }

  const criar = useMutation({
    mutationFn: async (f: FormPlan) => {
      const resposta = await (
        await api.plans.$post({ json: { name: f.name.trim(), description: f.description.trim() || null } })
      ).json()
      await gravarVinculos(resposta.plan.id, f.featureToggleIds)
    },
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Plano criado.' })
    },
    onError: aoFalhar,
  })

  const atualizar = useMutation({
    mutationFn: async ({ id, f }: { id: string; f: FormPlan }) => {
      await api.plans[':id'].$patch({
        param: { id },
        json: { name: f.name.trim(), description: f.description.trim() || null },
      })
      await gravarVinculos(id, f.featureToggleIds)
    },
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Plano atualizado.' })
    },
    onError: aoFalhar,
  })

  const alternarAtivo = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      (await api.plans[':id'].$patch({ param: { id }, json: { isActive } })).json(),
    onSuccess: invalidar,
    onError: aoFalhar,
  })

  const excluir = useMutation({
    mutationFn: async (id: string) => (await api.plans[':id'].$delete({ param: { id } })).json(),
    onSuccess: () => {
      invalidar()
      setConfirmandoExclusao(null)
      toast.push({ tone: 'success', message: 'Plano excluído.' })
    },
    onError: (erro) => {
      setConfirmandoExclusao(null)
      aoFalhar(erro)
    },
  })

  const plans = lista.data?.plans ?? []
  const emEdicao = editando?.id ? plans.find((p) => p.id === editando.id) : undefined
  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    if (termo === '') return plans
    return plans.filter((p) => `${p.name} ${p.description ?? ''}`.toLowerCase().includes(termo))
  }, [plans, busca])

  /** So toggles de natureza ENTITLEMENT entram na matriz do plano. */
  const togglesDePlano = (toggles.data?.toggles ?? []).filter((t) => t.kind === 'ENTITLEMENT')

  const abrirEditor = (alvo: PlanRow | null) => {
    setForm(
      alvo
        ? { name: alvo.name, description: alvo.description ?? '', featureToggleIds: alvo.featureToggleIds }
        : FORM_VAZIO,
    )
    setEditando({ id: alvo?.id ?? null })
  }

  /** Dirty check: nome, descricao e o conjunto de modulos. */
  const normalizado = (f: FormPlan) => ({
    name: f.name.trim(),
    description: f.description.trim(),
    featureToggleIds: [...f.featureToggleIds].sort(),
  })
  const alterado =
    editando?.id == null
      ? form.name.trim().length >= 2
      : emEdicao !== undefined &&
        JSON.stringify(normalizado(form)) !==
          JSON.stringify(
            normalizado({
              name: emEdicao.name,
              description: emEdicao.description ?? '',
              featureToggleIds: emEdicao.featureToggleIds,
            }),
          )

  // ------------------------------------------- formulário (FormScreen) ---

  if (editando !== null) {
    const salvarValido = alterado && form.name.trim().length >= 2 && !criar.isPending && !atualizar.isPending
    return (
      <FormScreen
        title={editando.id ? 'Editar plano' : 'Novo plano'}
        subtitle={editando.id ? emEdicao?.name : undefined}
        onClose={() => setEditando(null)}
        dirty={alterado}
        dirtyHint={editando.id ? 'Tudo salvo' : 'Preencha o nome para salvar'}
        saveLabel={editando.id ? 'Salvar' : 'Criar plano'}
        saveDisabled={!salvarValido}
        saving={criar.isPending || atualizar.isPending}
        onSubmit={() => {
          if (!salvarValido) return
          if (editando.id) atualizar.mutate({ id: editando.id, f: form })
          else criar.mutate(form)
        }}
      >
        <FormSection title="Informações do plano">
          <FormRow cols="1fr 2fr">
            <FieldBlock label="Nome do plano" required>
              {({ id, invalid }) => (
                <Input
                  id={id}
                  invalid={invalid}
                  autoFocus
                  value={form.name}
                  placeholder="Ex.: Essencial, Profissional, Completo"
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Descrição">
              {({ id }) => (
                <Input
                  id={id}
                  value={form.description}
                  placeholder="Para quem é este pacote"
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              )}
            </FieldBlock>
          </FormRow>
        </FormSection>

        <FormSection title={`Módulos inclusos${form.featureToggleIds.length > 0 ? ` (${form.featureToggleIds.length})` : ''}`}>
          <p className="adm-hint">
            O workspace neste plano só vê os módulos marcados. Módulo fora da lista some do menu e a API o bloqueia.
          </p>
          {toggles.isPending ? (
            <LoadingState variant="list" rows={3} label="Carregando os módulos" />
          ) : togglesDePlano.length === 0 ? (
            <p className="adm-hint">Nenhum toggle de módulo cadastrado — rode o seed da instalação.</p>
          ) : (
            <div className="adm-permgrid" role="group" aria-label="Módulos inclusos no plano">
              {togglesDePlano.map((t) => (
                <Checkbox
                  key={t.id}
                  label={rotuloDoToggle(t.code)}
                  checked={form.featureToggleIds.includes(t.id)}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      featureToggleIds: e.target.checked
                        ? [...f.featureToggleIds, t.id]
                        : f.featureToggleIds.filter((id) => id !== t.id),
                    }))
                  }
                />
              ))}
            </div>
          )}
        </FormSection>
      </FormScreen>
    )
  }

  // ------------------------------------------------------------ lista ---

  const cabecalho = (
    <div className="adm-head">
      <div>
        <h1 className="adm-head__title">Planos</h1>
        <p className="adm-head__desc">
          Pacotes de módulos que uma conta contrata. O plano é da conta (dono) e vale para todos os workspaces
          dela — escolhido na edição do workspace; trocar o pacote liga e desliga os módulos na hora.
        </p>
      </div>
      <Button variant="primary" icon="fas fa-plus" onClick={() => abrirEditor(null)}>
        Incluir
      </Button>
    </div>
  )

  if (lista.isPending) {
    return (
      <>
        {cabecalho}
        <LoadingState variant="list" label="Carregando os planos" />
      </>
    )
  }

  if (lista.isError) {
    const semPermissao = lista.error instanceof OrbindApiError && lista.error.status === 403
    return (
      <>
        {cabecalho}
        <div className="adm-placeholder">
          {semPermissao ? (
            <EmptyState
              icon="fas fa-lock"
              title="Sem permissão"
              description="Seu usuário não tem acesso à gestão de planos. Fale com um administrador da instalação."
            />
          ) : (
            <Alert
              tone="danger"
              title="Não foi possível carregar os planos"
              action={
                <Button variant="secondary" icon="fas fa-rotate-right" onClick={() => void lista.refetch()}>
                  Tentar de novo
                </Button>
              }
            >
              Verifique a conexão e tente novamente.
            </Alert>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      {cabecalho}

      <div className="adm-toolbar">
        <div className="adm-toolbar__search">
          <i className="fas fa-search" aria-hidden />
          <input
            type="search"
            placeholder="Filtrar por nome ou descrição…"
            aria-label="Filtrar planos"
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
          />
        </div>
        <span className="adm-toolbar__count">
          {filtrados.length} de {plans.length} {plans.length === 1 ? 'registro' : 'registros'}
        </span>
      </div>

      {filtrados.length === 0 ? (
        <div className="adm-placeholder">
          <EmptyState
            icon="fas fa-cubes"
            title={plans.length === 0 ? 'Nenhum plano ainda' : 'Nenhum plano com esse filtro'}
            description={
              plans.length === 0
                ? 'Crie o primeiro pacote de módulos — por exemplo, um plano Essencial com Demandas e Kanban, e um plano Pro com tudo.'
                : `Nada casa com “${busca}”.`
            }
            action={
              plans.length === 0 ? (
                <Button variant="primary" icon="fas fa-plus" onClick={() => abrirEditor(null)}>
                  Novo plano
                </Button>
              ) : (
                <Button variant="secondary" icon="fas fa-times" onClick={() => setBusca('')}>
                  Limpar o filtro
                </Button>
              )
            }
          />
        </div>
      ) : (
        <div className="adm-list">
          {filtrados.map((row) => {
            const confirmando = confirmandoExclusao === row.id
            const modulos = row.featureToggleIds.length
            const legenda = [
              row.description,
              `${modulos} ${modulos === 1 ? 'módulo' : 'módulos'}`,
              `${row.contaCount} ${row.contaCount === 1 ? 'conta' : 'contas'}`,
            ]
              .filter(Boolean)
              .join(' · ')
            return (
              <div key={row.id} className={`adm-catrow${row.isActive ? '' : ' adm-catrow--off'}`}>
                <span className="adm-catrow__icon">
                  <i className="fas fa-cubes" aria-hidden />
                </span>
                <span className="adm-catrow__main">
                  <span className="adm-catrow__name">{row.name}</span>
                  <span className="adm-catrow__sub">{legenda}</span>
                </span>
                {row.contaCount > 0 ? (
                  <Badge tone="info" icon="fas fa-building">
                    Em uso
                  </Badge>
                ) : null}

                {confirmando ? (
                  // confirmacao INLINE — sem modal, como o usuario prefere
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--ob-danger)' }}>Excluir “{row.name}”?</span>
                    <Button variant="danger" loading={excluir.isPending} onClick={() => excluir.mutate(row.id)}>
                      Confirmar
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirmandoExclusao(null)}>
                      Cancelar
                    </Button>
                  </span>
                ) : (
                  <>
                    <span className="adm-switchwrap">
                      <span className="adm-switchwrap__label">{row.isActive ? 'Ativo' : 'Inativo'}</span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={row.isActive}
                        aria-label={`${row.name} está ativo`}
                        title={row.isActive ? 'Ativo — clique para desativar' : 'Inativo — clique para ativar'}
                        className={`adm-switch${row.isActive ? ' adm-switch--on' : ''}`}
                        onClick={() => alternarAtivo.mutate({ id: row.id, isActive: !row.isActive })}
                      >
                        <span className="adm-switch__knob" aria-hidden />
                      </button>
                    </span>

                    <button
                      type="button"
                      className="adm-iconbtn"
                      aria-label={`Editar ${row.name}`}
                      title="Editar"
                      onClick={() => abrirEditor(row)}
                    >
                      <i className="fas fa-pen" aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="adm-iconbtn adm-iconbtn--danger"
                      disabled={row.contaCount > 0}
                      aria-label={
                        row.contaCount > 0
                          ? `${row.name} está em uso e não pode ser excluído`
                          : `Excluir ${row.name}`
                      }
                      title={
                        row.contaCount > 0
                          ? 'Plano em uso: mova as contas para outro plano antes de excluir'
                          : 'Excluir'
                      }
                      onClick={() => setConfirmandoExclusao(row.id)}
                    >
                      <i className="fas fa-trash-alt" aria-hidden />
                    </button>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}
